import { useState, useRef, useEffect } from 'react';
import { initializeMap } from '../MapInitializer';

/**
 * Custom hook to manage map state
 * Handles map initialization, cleanup, and state management
 * 
 * @param {boolean} showDistanceLabels - Flag to indicate if distance labels should be shown
 * @returns {Object} Map state and references
 */
export const useMapState = (showDistanceLabels) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const sourcesReadyRef = useRef(false); // Track if data sources are ready
  const cleanupFnRef = useRef(null); // Store cleanup function reference
  
  // Track component mount status
  const isMountedRef = useRef(true);
  
  // Handle component mount/unmount
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialize map
  useEffect(() => {
    // Add debug logs

    // First clean up existing map
    if (map.current) {

      if (cleanupFnRef.current) {
        cleanupFnRef.current();
      }
      map.current.remove();
      map.current = null;
      setMapReady(false);
      sourcesReadyRef.current = false;
    }
    
    // Ensure component is still mounted
    if (!isMountedRef.current) {
      return;
    }
    
    // Ensure mapContainer is attached to DOM
    if (!mapContainer.current) {
      return;
    }

    const mapInitResult = initializeMap({
      mapContainer,
      setMapError,
      setMapReady,
      sourcesReadyRef,
      shouldShowDistanceLabels: true // Always initialize distance labels, control visibility via layer properties
    });
    
    if (mapInitResult) {

      map.current = mapInitResult.map;
      const resizeObserver = mapInitResult.resizeObserver;
      cleanupFnRef.current = mapInitResult.cleanup;
      
      return () => {

        if (mapContainer.current) {
          resizeObserver.unobserve(mapContainer.current);
        }
        
        if (cleanupFnRef.current) {
          cleanupFnRef.current();
        }
        
        if (map.current) {
          try {
            map.current.remove();
          } catch (error) {
            console.error('Error removing map:', error);
          }
          map.current = null;
          setMapReady(false);
          sourcesReadyRef.current = false;
        }
      };
    } else {

      setMapError('Failed to initialize map');
    }
  }, []); // Map should only be initialized once, not on distance label changes

  return {
    mapContainer,
    map,
    mapError,
    mapReady,
    sourcesReadyRef,
    isMountedRef
  };
}; 